import { useCallback, useEffect, useRef, useState } from 'react';
import { resolveBand, BANDS, type BandId } from '@/domain/bands';
import {
  countWithin,
  rankStations,
  roundTo,
  COORD_DECIMALS,
  type Coords,
  type Station,
  type StationWithDistance,
} from '@/domain/geo';
import { geocodeCep as defaultGeocode, type GeocodeResult } from '@/services/geocoding';
import { track } from '@/services/analytics';

export type LookupResult = {
  band: BandId;
  nearest: StationWithDistance;
  withinCount: number;
  withinRadiusKm: number;
  coords: Coords;
  label: string;
  cep: string | null;
};

export type LookupErrorKind =
  | 'invalid_cep'
  | 'cep_not_found'
  | 'imprecise'
  | 'unavailable'
  | 'geolocation_denied'
  | 'geolocation_unavailable'
  | 'no_stations';

export type LookupState = 'idle' | 'busy' | 'ready' | 'error';

type UseLookupParams = {
  stations: readonly Station[];
  geocode?: (cep: string) => Promise<GeocodeResult>;
  geolocate?: () => Promise<Coords>;
};

const PERMISSION_DENIED = 1;

/* The spec's GeolocationPositionError is not a global in jsdom, so `instanceof`
   would throw during tests. Recognise the shape the callback actually rejects with,
   and treat anything unrecognisable as a denial: that is the message that tells the
   lead what to do next. */
const isPositionError = (value: unknown): value is { code: number } =>
  value instanceof Object && 'code' in value && typeof value.code === 'number';

const browserGeolocate = (): Promise<Coords> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
      (error) => reject(error),
      { timeout: 10000, maximumAge: 60000 },
    );
  });

export const useLookup = ({
  stations,
  geocode = defaultGeocode,
  geolocate = browserGeolocate,
}: UseLookupParams) => {
  const [state, setState] = useState<LookupState>('idle');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [errorKind, setErrorKind] = useState<LookupErrorKind | null>(null);

  /* What the lead gave us, kept so the answer can be recomputed against a network that
     lands later. The band the lead is reading, kept so a recompute reports itself only
     when it actually changes what they see. */
  const resolved = useRef<{ coords: Coords; label: string; cep: string | null } | null>(null);
  const shownBand = useRef<BandId | null>(null);

  const fail = useCallback((kind: LookupErrorKind) => {
    /* Forget the input as well. A recompute against a later network would otherwise
       revive the previous answer on top of this error, and the page would show a station
       for a CEP it had just said it could not find. */
    resolved.current = null;
    shownBand.current = null;
    setResult(null);
    setErrorKind(kind);
    setState('error');
  }, []);

  const succeed = useCallback(
    (coords: Coords, label: string, cep: string | null) => {
      resolved.current = { coords, label, cep };
      const ranked = rankStations(coords, stations);
      const nearest = ranked[0];
      if (!nearest) {
        fail('no_stations');
        return;
      }
      const band = resolveBand(nearest.distanceKm);
      setErrorKind(null);
      setResult({
        band,
        nearest,
        withinCount: countWithin(ranked, BANDS.limitedFromKm),
        withinRadiusKm: BANDS.limitedFromKm,
        coords,
        label,
        cep,
      });
      setState('ready');
      if (band !== shownBand.current) {
        shownBand.current = band;
        track('sf_band_shown', { band, source: cep === null ? 'geolocation' : 'cep' });
      }
    },
    [fail, stations],
  );

  const submitCep = useCallback(
    async (cep: string) => {
      setState('busy');
      /* Emitted on submission, not on success: a CEP the geocoder cannot resolve is
         still a lead who asked, and where those land says where coverage is thin. */
      track('sf_location_submitted', { input_type: 'cep', cep });
      const geocoded = await geocode(cep);
      if (geocoded.status !== 'ok') {
        if (geocoded.status === 'imprecise') {
          track('sf_geocode_degraded', { reason: 'imprecise' });
        }
        fail(geocoded.status);
        return;
      }
      const place = [geocoded.neighborhood, geocoded.city].filter((part) => part !== '').join(', ');
      succeed(geocoded.coords, place === '' ? geocoded.city : place, cep);
    },
    [fail, geocode, succeed],
  );

  const useLocation = useCallback(async () => {
    setState('busy');
    track('sf_geolocation_used');
    try {
      const coords = await geolocate();
      track('sf_location_submitted', {
        input_type: 'geolocation',
        latitude: roundTo(coords.lat, COORD_DECIMALS),
        longitude: roundTo(coords.lon, COORD_DECIMALS),
      });
      succeed(coords, '', null);
    } catch (error) {
      const denied = !isPositionError(error) || error.code === PERMISSION_DENIED;
      fail(denied ? 'geolocation_denied' : 'geolocation_unavailable');
    }
  }, [fail, geolocate, succeed]);

  /* The bundled snapshot answers first and the live endpoint lands about 1.2 s later, so
     the first answer can be measured against a network that no longer exists. Recompute
     when the list changes: on the day a station opens in a new city, a lead there stops
     being told the nearest one is 90 km away. */
  useEffect(() => {
    if (resolved.current) {
      const { coords, label, cep } = resolved.current;
      succeed(coords, label, cep);
    }
  }, [succeed]);

  const reset = useCallback(() => {
    resolved.current = null;
    shownBand.current = null;
    setResult(null);
    setErrorKind(null);
    setState('idle');
  }, []);

  return { state, result, errorKind, submitCep, useLocation, reset };
};
